/**
 * ConversationRow Component
 *
 * Displays a conversation (email, whatsapp, etc.) in row format.
 * Used in TodayView for conversations awaiting response.
 */

import React, { useMemo, useState } from 'react';
import { Mail, MessageCircle, Phone } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { useTranslation } from '../../hooks';
import type { ActionType } from '../../types/row.types'; // Still needed for availableActions

// =============================================================================
// TYPES
// =============================================================================

export interface Conversation {
  id: string;
  channel: 'email' | 'whatsapp' | 'telegram' | 'call' | 'sms' | string;
  title: string;
  lastAt: string;
  contactId?: string;
  contactName?: string;
  unread?: number;
  projectId?: string;
}

export interface ConversationRowProps {
  entity: Conversation;
  onTitleClick?: (conversation: Conversation) => void;
  onActionClick?: (actionType: string, conversationId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ConversationRow({ entity: conversation, onTitleClick, onActionClick, onAvatarClick }: ConversationRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware time formatting
  const formatTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Primary action state (archived)
  const [isArchived, setIsArchived] = useState(false);

  // Left slot - Primary Action + Contact Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="archive"
        active={isArchived}
        onChange={(archived) => {
          setIsArchived(archived);
          onActionClick?.('archive', conversation.id);
        }}
      />
      <EntityAvatar
        items={conversation.contactName ? { id: conversation.contactId, name: conversation.contactName } : []}
        type="person"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get channel icon
  const icon = useMemo(() => {
    const iconProps = { size: 16 };
    switch (conversation.channel) {
      case 'email':
        return <Mail {...iconProps} style={{ color: '#FF6B2D' }} />;
      case 'whatsapp':
        return <MessageCircle {...iconProps} style={{ color: '#25D366' }} />;
      case 'telegram':
        return <MessageCircle {...iconProps} style={{ color: '#0088cc' }} />;
      case 'call':
        return <Phone {...iconProps} style={{ color: '#1D5BBF' }} />;
      default:
        return <MessageCircle {...iconProps} style={{ color: '#00C853' }} />;
    }
  }, [conversation.channel]);

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Contact name
    if (conversation.contactName) {
      parts.push(
        <span key="contact" style={styles.contact}>
          {conversation.contactName}
        </span>
      );
    }

    // Channel badge
    parts.push(
      <span key="channel" style={{ ...styles.badge, ...getChannelStyle(conversation.channel) }}>
        {t(`hub.channels.${conversation.channel}`)}
      </span>
    );

    // Unread count
    if (conversation.unread && conversation.unread > 0) {
      parts.push(
        <span key="unread" style={styles.unreadBadge}>
          {t('hub.unreadCount', { count: conversation.unread })}
        </span>
      );
    }

    // Time
    parts.push(
      <span key="time" style={styles.time}>
        {formatTime(conversation.lastAt)}
      </span>
    );

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [conversation, t, formatTime]);

  // Available actions
  const availableActions: ActionType[] = ['email', 'call', 'task', 'note'];

  return (
    <BaseRow
      entity={{ ...conversation, title: conversation.title }}
      entityType="conversation"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={{} as any}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isArchived}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// getChannelLabel is now handled by translation t(`hub.channels.${channel}`)

function getChannelStyle(channel: string): React.CSSProperties {
  const channelStyles: Record<string, React.CSSProperties> = {
    email: { background: '#fff4ed', color: '#FF6B2D' },
    whatsapp: { background: '#e8f5e9', color: '#25D366' },
    telegram: { background: '#e3f2fd', color: '#0088cc' },
    call: { background: '#e8eaf6', color: '#1D5BBF' },
    sms: { background: '#f5f5f5', color: '#666' },
  };
  return channelStyles[channel] || { background: '#f0f0f0', color: '#666' };
}

// formatTime is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
  } as React.CSSProperties,
  contact: {
    fontWeight: 500,
    color: '#374151',
  } as React.CSSProperties,
  badge: {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 500,
  } as React.CSSProperties,
  unreadBadge: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 500,
  } as React.CSSProperties,
  time: {
    color: '#999',
  } as React.CSSProperties,
};
